import { useMemo, useCallback } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useExerciseForm } from 'modules/exercises/hooks';

import { MultipleChoiceExerciseInput, MultipleChoiceQuestionInput, MultipleChoiceOptionInput, MultipleChoiceOptionType } from '../types/form';
import { MultipleChoiceExercise } from '../types';
import { transformMultipleChoiceExerciseToFormInput } from '../utils';

interface MultipleChoiceExercisesFormHookInput {
  exerciseIndex: number;
  exercise?: MultipleChoiceExercise;

  defaultIncorrectOptionType: 'negative' | 'neutral';
  defaultNumberOfOptions: number;
}

interface MultipleChoiceExercisesFormHookOutput {
  useFormProps: UseFormReturn<MultipleChoiceExerciseInput>;

  getNextQuestion: (index: number) => MultipleChoiceQuestionInput;
  getNextOption: (index: number) => MultipleChoiceOptionInput;
  canAddMoreOptions: (count: number) => boolean;

  allOptionTypes: MultipleChoiceOptionType[];

  initialQuestionNumber: number;
  onChangeInitialQuestionNumber: (value: number) => void;
}

const multipleChoiceDefaultQuestionOptions = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];

export default function useMultipleChoiceExercisesForm(input: MultipleChoiceExercisesFormHookInput): MultipleChoiceExercisesFormHookOutput {
  const { exerciseIndex = 0, exercise, defaultIncorrectOptionType, defaultNumberOfOptions } = input;
  const intl = useIntl();
  const { getDefaultExerciseData, getDefaultQuestionTitle, initialQuestionNumber, onChangeInitialQuestionNumber } = useExerciseForm();

  const allOptionTypes = useMemo((): MultipleChoiceOptionType[] => {
    return [
      { title: intl.formatMessage({ id: 'answerType.positive' }), value: 'positive' },
      { title: intl.formatMessage({ id: 'answerType.neutral' }), value: 'neutral' },
      { title: intl.formatMessage({ id: 'answerType.negative' }), value: 'negative' },
    ];
  }, [intl]);

  const getNextOption = useCallback(
    (index: number): MultipleChoiceOptionInput => {
      return { text: multipleChoiceDefaultQuestionOptions[index], type: defaultIncorrectOptionType };
    },
    [defaultIncorrectOptionType]
  );

  const getNextQuestion = useCallback(
    (index: number): MultipleChoiceQuestionInput => {
      /**
       * Get default options count from course
       */
      const options = [...Array(defaultNumberOfOptions)].map((_, index) => getNextOption(index));
      return {
        text: getDefaultQuestionTitle(index),
        options,
      };
    },
    [getNextOption, getDefaultQuestionTitle, defaultNumberOfOptions]
  );

  const getDefaultValue = (): MultipleChoiceExerciseInput => {
    if (exercise) {
      return transformMultipleChoiceExerciseToFormInput(exercise);
    }

    const coreData = getDefaultExerciseData(exerciseIndex);
    const question = getNextQuestion(0);
    return {
      ...coreData,
      questions: [question],
    };
  };

  const canAddMoreOptions = useCallback((count: number): boolean => {
    return count < 13;
  }, []);

  const useFormProps = useForm<MultipleChoiceExerciseInput>({ defaultValues: getDefaultValue() });
  const { setValue, getValues } = useFormProps;

  const handleChangeInitialQuestionNumber = useCallback(
    (newInitialQuestionNumber: number) => {
      const questionsCount = getValues('questions')?.length || 0;
      onChangeInitialQuestionNumber(newInitialQuestionNumber, questionsCount, (index: number, title: string) => {
        setValue(`questions.${index}.text`, title);
      });
    },
    [onChangeInitialQuestionNumber, getValues, setValue]
  );

  return {
    useFormProps,

    getNextQuestion,
    initialQuestionNumber,
    onChangeInitialQuestionNumber: handleChangeInitialQuestionNumber,

    getNextOption,
    canAddMoreOptions,
    allOptionTypes,
  };
}
